/**
 * Much of this file contains logic from leesa-next, mostly unmodified except
 * that it is now split into smaller functions for slightly better readability.
 * However, it is still quite complex and could use refactoring especially in CS.
 */
import { productDecode } from '@/lib/shopify/gid';

/**
 * Creates a new "itemsToAdd" array with GWP items included in the correct place.
 * If the selected product is not eligible for a GWP item, this returns just the
 * original array, unmodified.
 * @param itemListToUpdate The array of items we plan to add to cart that we
 *  will enhance with GWP items.
 * @param gifts_with_purchase The contentstack settings object that contains all
 *  the GWP settings.
 * @param selectedVariant The variant that the user has selected to add to cart,
 *   or, seen another way, the variant that is the parent of the GWP items.
 * @returns A new array with GWP items included in itemListToUpdate.
 */
export function mergeGwpItemsIntoList(
  itemListToUpdate,
  gifts_with_purchase,
  selectedVariant
) {
  if (!gifts_with_purchase) return itemListToUpdate;

  const eligibleProduct = findEligibleProduct(
    selectedVariant,
    gifts_with_purchase
  );
  if (!eligibleProduct) return itemListToUpdate;

  // Get the list of GWP options from the option manager settings object
  // (Return early if there are no options)
  const optionList = getOptionList(gifts_with_purchase, eligibleProduct);
  if (!optionList) return itemListToUpdate;

  const matchedOption = findMatchedGwpOption(optionList, selectedVariant);
  if (!matchedOption) return itemListToUpdate;

  const allGwp = buildGwpItems(matchedOption);
  return insertAllGwpItems(allGwp, itemListToUpdate, selectedVariant);
}

/**
 * Finds the product that is eligible for the GWP in the gifts_with_purchase
 * settings object.
 */
function findEligibleProduct(selectedVariant, gifts_with_purchase) {
  return gifts_with_purchase?.eligible_products.find((product) => {
    return (
      product.product_eligible_for_gwpConnection.shopifyProduct ===
      selectedVariant?.productId
    );
  });
}

/**
 * Gets the full list of gwp options from the option manager settings object
 * that allows us to know what gwp items are available for the selected product.
 */
function getOptionList(gifts_with_purchase, eligibleProduct) {
  const optionManager = gifts_with_purchase?.gwp_option_manager?.option_manager;
  if (optionManager?.length < 1) return null;

  // Get the correct option manager instance, which is sort of like the index
  // of the GWP option manager array that we want to use.
  const optionManagerInstance = gifts_with_purchase?.gwp_option_manager[
    eligibleProduct?.option_manager_instance - 1
  ]
    ? eligibleProduct?.option_manager_instance - 1
    : 0;

  return optionManager[optionManagerInstance];
}

// Finds the GWP option that matches the selected product's variant title
function findMatchedGwpOption(optionList, selectedVariant) {
  return optionList?.gwp_size_options?.find(
    (option) =>
      // Watch out! gwpItem.size is the variant title of the eligible product.
      // Not just the size, and not the gwp's size.
      option.size === (selectedVariant?.title || selectedVariant?.variantTitle)
  );
}

// Builds GWP items based on matched options
function buildGwpItems(matchedOption) {
  const allGwp = [];

  const gwpTitle = getGwpTitle(matchedOption);

  const gwpObj = createGwpObject(gwpTitle, matchedOption);
  if (gwpObj.product) {
    allGwp.push(gwpObj);
  }

  if (matchedOption?.additional_gwp) {
    const additionalGwpObj = createGwpObject(gwpTitle, matchedOption, true);
    allGwp.push(additionalGwpObj);
  }

  return allGwp;
}

/**
 * Determines GWP title based on size and option.
 */
function getGwpTitle(gwpItem) {
  // The variant title of the selected product, not the GWP product
  const selectedSize = gwpItem?.size.includes(' / ')
    ? gwpItem?.size.split(' / ')[0]
    : gwpItem?.size;

  // If there is a color option for the gwp product, and a gwp size, set the gwp
  // variant name as gwp size / color option.
  if (gwpItem?.option !== null && gwpItem?.gwp_size?.length) {
    return `${gwpItem?.gwp_size} / ${gwpItem?.option}`;

    // If there is a color option and no gwp size, use option size / color option
  } else if (gwpItem?.option !== null) {
    return `${selectedSize} / ${gwpItem?.option}`;

    // If a gwp size is preset in the settings, use that
  } else if (gwpItem?.gwp_size) {
    return gwpItem?.gwp_size;

    // Lastly, use the size that matches the product as a title
  } else {
    return selectedSize;
  }
}

function createGwpObject(gwpTitle, gwpItem, isAdditional = false) {
  return {
    title: gwpTitle,
    quantity: gwpItem.gwp_quantity,
    product: isAdditional
      ? gwpItem.additional_gwp
      : gwpItem.gwp_productConnection
  };
}

/**
 * Creates a new itemsToAdd array with all GWP payloads in the correct place,
 * that is, after the selected product.
 */
function insertAllGwpItems(allGwp, itemListToUpdate, selectedVariant) {
  return allGwp.reduce((updatedList, gwpData) => {
    const gwpVariant = findGwpVariant(gwpData);
    const imageUrl = getGwpImage(gwpData, gwpVariant);

    const gwpPayload = createGwpPayload(
      gwpData,
      selectedVariant,
      gwpVariant,
      imageUrl
    );

    return insertGwpItemAfterParent(updatedList, selectedVariant, gwpPayload);
  }, itemListToUpdate);
}

function findGwpVariant(gwpData) {
  const gwpProduct = gwpData?.product;

  const isMatchingGwpVariant = gwpProduct?.shopifyProduct?.variants.some(
    (item) => item.title.split(' / ')[0] === gwpData.title.split(' / ')[0]
  );

  return isMatchingGwpVariant
    ? gwpProduct?.shopifyProduct?.variants.find(
        (variant) => variant.title === gwpData.title
      ) || gwpProduct?.shopifyProduct?.variants[0]
    : gwpProduct?.shopifyProduct?.variants[0];
}

function getGwpImage(gwpData, gwpVariant) {
  const gwpProduct = gwpData?.product;

  return gwpVariant?.title.includes(' / ') &&
    gwpProduct?.product_image?.image_collections.length > 1
    ? gwpProduct?.product_image?.image_collections.find((imageOptions) =>
        imageOptions?.thumbnail.url.includes(
          gwpData.title.split(' / ')[1]?.replace(' ', '').toLowerCase()
        )
      )?.thumbnail.url
    : gwpProduct?.product_image[0]?.url;
}

function createGwpPayload(gwpData, selectedVariant, gwpVariant, imageUrl) {
  return {
    variant: gwpVariant,
    product: gwpData.product,
    quantity: parseInt(gwpData?.quantity, 10) || 1,
    customAttributes: [
      { key: '_isPromo', value: 'true' },
      { key: '_promoType', value: 'free' },
      { key: '_promoParent', value: selectedVariant.id }, // Should this be the product ID?
      { key: '_productImage', value: imageUrl },
      { key: '_productPosition', value: '0' },
      { key: '_productTitle', value: gwpData?.product?.shopifyProduct?.title },
      {
        key: '_productType',
        value: gwpData?.product?.shopifyProduct?.productType || 'default'
      }, // might have to add a default type in case its null or " "
      {
        key: '_compareAtPrice',
        value: gwpVariant?.compareAtPrice?.amount || '0'
      },
      {
        key: '_validPromoMultiplier',
        value: gwpData?.quantity?.toString() || '1'
      },
      {
        key: '_promoParentVariantId',
        value: productDecode(selectedVariant.id).id
      },
      { key: '_gwpQuantity', value: gwpData?.quantity?.toString() || '1' }
    ]
  };
}

/**
 * Creates a new itemsToAdd array with the GWP payload in the correct place.
 */
function insertGwpItemAfterParent(
  itemListToUpdate,
  selectedVariant,
  gwpPayload
) {
  const parentIndex = itemListToUpdate.findIndex(
    (item) => item.selectedVariantId === selectedVariant.id
  );

  if (parentIndex !== -1) {
    return [
      ...itemListToUpdate.slice(0, parentIndex + 1),
      gwpPayload,
      ...itemListToUpdate.slice(parentIndex + 1)
    ];
  }

  return [...itemListToUpdate, gwpPayload];
}
